import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import apiService from "../../../../../service/Api";
import { Close } from "@mui/icons-material";

interface LeaderboardUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  // if backend still returns venue_count for contacts leaderboard, keep it.
  // Ideally you'd have contact_count. We'll support either:
  venue_count?: number; // fallback name used in your venues leaderboard response
  contact_count?: number;
  average_venue_score?: number; // not used here
}

type ContactCreatedBy = {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
};

interface ContactApi {
  id: number;
  contact_type?: string; // individual/company
  contact_source?: string;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  suburb?: string | null;
  address_one?: string | null;
  contact_image?: string | null;
  created_by?: ContactCreatedBy;
  created_at?: string;
  updated_at?: string;
}

interface ContactsApiResponse {
  results: ContactApi[];
}

interface LeaderboardResponse {
  leaderboard: LeaderboardUser[];
  total_participants: number;
}

const getFullName = (u: {
  first_name?: string;
  last_name?: string;
  email?: string;
}) => {
  const full = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  return full.length ? full : (u.email ?? "Unknown");
};

const getContactDisplayName = (c: ContactApi) => {
  // prefer "name" if contact_type is company or if name exists
  if (c.name && c.name.trim().length) return c.name.trim();

  const full = `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim();
  if (full.length) return full;

  return c.email?.trim() || c.phone?.trim() || "Unnamed Contact";
};

const getContactLocation = (c: ContactApi) =>
  [c.address_one, c.suburb, c.city].filter(Boolean).join(", ") || "—";

const countForUser = (u: LeaderboardUser) =>
  typeof u.contact_count === "number"
    ? u.contact_count
    : typeof u.venue_count === "number"
      ? u.venue_count
      : 0;

const ContactsLeaderboard: React.FC = () => {
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedUser, setSelectedUser] = useState<LeaderboardUser | null>(
    null,
  );
  const [userContacts, setUserContacts] = useState<ContactApi[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        setError(null);

        // 🔁 CHANGE THIS if your contacts leaderboard endpoint differs
        const res = await apiService.get<LeaderboardResponse>(
          "/api/management/dashboard/contacts-leaderboard/",
        );

        setData(res.data);
      } catch (err: unknown) {
        setError(
          (err as { message?: string })?.message ||
            "Failed to fetch contacts leaderboard.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, []);

  const handleUserClick = async (user: LeaderboardUser) => {
    setSelectedUser(user);
    setPage(0);
    setContactsLoading(true);

    try {
      const res = await apiService.get<ContactsApiResponse>(
        `/api/contacts/contact/?created_by=${user.id}&page_size=1000`,
      );
      setUserContacts(res.data.results || []);
    } catch (err) {
      console.error(err);
      setUserContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const paginatedContacts = useMemo(
    () =>
      userContacts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [userContacts, page, rowsPerPage],
  );

  return (
    <>
      <Card sx={{ p: 3, borderRadius: 3, boxShadow: 0 }}>
        <Typography variant="h5" fontWeight={700} mb={3}>
          Contacts Leaderboard
        </Typography>

        {loading && <Typography>Loading...</Typography>}
        {error && <Typography color="error">{error}</Typography>}

        <Box sx={{ display: "flex", justifyContent: "space-between", px: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            CREATOR
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            CONTACTS
          </Typography>
        </Box>

        <List>
          {data?.leaderboard?.map((entry, index) => {
            const fullName = getFullName(entry);

            return (
              <Box
                key={entry.id}
                onClick={() => handleUserClick(entry)}
                sx={{ cursor: "pointer" }}
              >
                <ListItem
                  sx={{
                    bgcolor: "#f7f7f7",
                    borderRadius: 2,
                    my: 1,
                    px: 2,
                    py: 1.5,
                  }}
                  secondaryAction={
                    <Typography fontWeight={600}>
                      {countForUser(entry)}
                    </Typography>
                  }
                >
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    sx={{ width: 30 }}
                  >
                    {index + 1}.
                  </Typography>

                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "grey.400" }}>
                      {fullName.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={fullName}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              </Box>
            );
          })}
        </List>
      </Card>

      {/* MODAL */}
      <Dialog
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        fullWidth
        maxWidth="md"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <DialogTitle>
            {`${getFullName(selectedUser ?? {})}'s Contacts`}
          </DialogTitle>
          <IconButton sx={{ mx: 3 }} onClick={() => setSelectedUser(null)}>
            <Close />
          </IconButton>
        </Box>

        <DialogContent>
          {contactsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          ) : userContacts.length === 0 ? (
            <Typography sx={{ textAlign: "center", py: 3 }}>
              This user has no contacts.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f7f7f7" }}>
                    <TableCell>Image</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Location</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedContacts.map((c) => (
                    <TableRow
                      key={c.id}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#f5f5f5" },
                        height: 60,
                      }}
                      // 🔁 Change route if yours is different
                      onClick={() => navigate(`/contacts/view/${c.id}`)}
                    >
                      <TableCell>
                        {c.contact_image ? (
                          <Avatar
                            variant="rounded"
                            src={c.contact_image}
                            sx={{ width: 30, height: 30 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 30,
                              height: 30,
                              bgcolor: "grey.300",
                              borderRadius: 1,
                            }}
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography fontWeight={600}>
                          {getContactDisplayName(c)}
                        </Typography>
                      </TableCell>

                      <TableCell>{c.email || "—"}</TableCell>
                      <TableCell>{c.phone || "—"}</TableCell>
                      <TableCell>{c.contact_type || "—"}</TableCell>
                      <TableCell>{getContactLocation(c)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={userContacts.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContactsLeaderboard;
